import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, CheckCircle2, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApp } from "@/context/AppContext";
import { PageHeader, Panel, Loading, Empty, Kpi, Money, Th, Td, TableWrap } from "@/components/kit";
import { dmy, dmyTime, todayIso, errMsg } from "@/lib/format";

const RateTable = ({ rows, t }) => (rows.length === 0 ? <Empty /> : (
  <TableWrap>
    <thead className="border-b"><tr><Th>{t("vat_rate")}</Th><Th className="text-end">{t("doc_count")}</Th><Th className="text-end">{t("base")}</Th><Th className="text-end">{t("total_vat")}</Th></tr></thead>
    <tbody>{rows.map((r) => <tr key={r.rate} className="border-b border-slate-50" data-testid={`decl-rate-${r.rate}`}><Td>{r.rate} %</Td><Td className="text-end num">{r.count}</Td><Td className="text-end"><Money v={r.base} /></Td><Td className="text-end"><Money v={r.vat} className="font-semibold" /></Td></tr>)}</tbody>
  </TableWrap>
));

const WhTable = ({ rows, t }) => (rows.length === 0 ? <Empty /> : (
  <TableWrap>
    <thead className="border-b"><tr><Th>{t("code")}</Th><Th>{t("label")}</Th><Th className="text-end">{t("doc_count")}</Th><Th className="text-end">{t("base")}</Th><Th className="text-end">{t("amount")}</Th></tr></thead>
    <tbody>{rows.map((r) => <tr key={r.code} className="border-b border-slate-50"><Td className="num text-xs">{r.code}</Td><Td className="text-xs">{r.label} ({r.rate} %)</Td><Td className="text-end num">{r.count}</Td><Td className="text-end"><Money v={r.base} /></Td><Td className="text-end"><Money v={r.amount} className="font-semibold" /></Td></tr>)}</tbody>
  </TableWrap>
));

const CarryPanel = ({ d, onSaved }) => {
  const { api, t, can } = useApp();
  const [op, setOp] = useState({ month: d.month, amount: "" });
  useEffect(() => { api.get("/declarations/opening").then((r) => r.data.month && setOp({ month: r.data.month, amount: r.data.amount })); }, [api]);
  const save = async () => {
    try { await api.put("/declarations/opening", { month: op.month, amount: +op.amount || 0 }); toast.success(t("saved")); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  const net = d.vat_collected_total - d.vat_deductible_total - d.credit_carried;
  return (
    <Panel title={t("carry_title")} testid="decl-carry">
      <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between"><dt>{t("vat_collected")}</dt><dd><Money v={d.vat_collected_total} /></dd></div>
          <div className="flex justify-between"><dt>− {t("vat_deductible")}</dt><dd><Money v={d.vat_deductible_total} /></dd></div>
          <div className="flex justify-between"><dt>− {t("credit_carried")}</dt><dd data-testid="decl-credit-carried"><Money v={d.credit_carried} /></dd></div>
          <div className={`flex justify-between border-t pt-2 font-semibold ${net < 0 ? "text-emerald-700" : "text-slate-900"}`}>
            <dt>{net < 0 ? t("credit_to_carry") : t("vat_due")}</dt><dd data-testid="decl-credit-to-carry"><Money v={net < 0 ? d.credit_to_carry : d.vat_due} /></dd>
          </div>
        </dl>
        {can("declarations", true) && (
          <div className="rounded-lg bg-slate-50 p-3">
            <div className="eyebrow mb-2">{t("vat_opening")}</div>
            <div className="flex gap-2">
              <Input type="month" value={op.month} onChange={(e) => setOp({ ...op, month: e.target.value })} className="h-8" data-testid="decl-opening-month" />
              <Input type="number" step="0.001" value={op.amount} onChange={(e) => setOp({ ...op, amount: e.target.value })} className="h-8 w-28" data-testid="decl-opening-amount" />
              <Button size="sm" onClick={save} data-testid="decl-opening-save">{t("save")}</Button>
            </div>
            <p className="mt-2 text-[11px] text-slate-500">{t("vat_opening_help")}</p>
          </div>
        )}
      </div>
    </Panel>
  );
};

export default function Declarations() {
  const { api, t, can, download, company } = useApp();
  const [month, setMonth] = useState(todayIso().slice(0, 7));
  const [d, setD] = useState(null);
  const [history, setHistory] = useState([]);
  const [ref, setRef] = useState("");
  const load = useCallback(() => {
    setD(null);
    api.get("/declarations/monthly", { params: { month } }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/declarations").then((r) => setHistory(r.data));
  }, [api, month]);
  useEffect(() => { load(); }, [load]);
  const remind = async () => {
    try { const { data } = await api.post(`/declarations/${month}/remind`); toast.success(`${t("decl_reminded")} : ${data.sent.join(", ")}`); } catch (e) { toast.error(errMsg(e)); }
  };
  const file = async () => {
    try { await api.post(`/declarations/${month}/file`, { reference: ref }); toast.success(t("decl_filed")); setRef(""); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <PageHeader eyebrow={company.name} title={t("declarations")}>
        <Input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="w-44" data-testid="decl-month" />
        <Button variant="outline" onClick={() => download(`/declarations/monthly/export?month=${month}&format=xlsx`, `declaration_${month}.xlsx`)} data-testid="decl-export-xlsx"><Download className="h-4 w-4 me-1" />Excel</Button>
        <Button variant="outline" onClick={() => download(`/declarations/monthly/export?month=${month}&format=pdf`, `declaration_${month}.pdf`, false)} data-testid="decl-export-pdf"><Download className="h-4 w-4 me-1" />PDF</Button>
      </PageHeader>
      <p className="text-xs text-slate-500">{t("decl_disclaimer")}</p>
      {!d ? <Loading /> : (
        <>
          <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm" data-testid="decl-status">
            <CalendarClock className="h-4 w-4 text-orange-700" />{t("decl_deadline")} : <b>{dmy(d.deadline)}</b>
            {d.filed ? (
              <span className="ms-auto flex items-center gap-1.5 font-semibold text-emerald-700" data-testid="decl-filed-badge"><CheckCircle2 className="h-4 w-4" />{t("decl_filed")} · {dmyTime(d.filed.filed_at)} {d.filed.reference && `· ${d.filed.reference}`}</span>
            ) : can("declarations", true) && (
              <div className="ms-auto flex items-center gap-2">
                <Input placeholder={t("decl_reference")} value={ref} onChange={(e) => setRef(e.target.value)} className="h-8 w-52" data-testid="decl-reference" />
                <Button size="sm" onClick={file} data-testid="decl-file-btn">{t("decl_mark_filed")}</Button>
                <Button size="sm" variant="outline" onClick={remind} data-testid="decl-remind-btn">{t("decl_remind")}</Button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi testid="decl-vat-due" label={t("vat_due")} value={<Money v={d.vat_due} />} sub={d.credit_to_carry > 0 ? <>{t("credit_to_carry")} : <Money v={d.credit_to_carry} /></> : d.credit_carried > 0 ? <>{t("credit_carried")} : <Money v={d.credit_carried} /></> : null} />
            <Kpi testid="decl-stamp" label={t("stamp")} value={<Money v={d.stamp.amount} />} sub={`${d.stamp.count} ${t("doc_count").toLowerCase()}`} accent="navy" />
            <Kpi testid="decl-wh" label={t("wh_operated")} value={<Money v={d.withholding_operated_total} />} accent="orange" />
            <Kpi testid="decl-total" label={t("total_to_pay")} value={<Money v={d.total_to_pay} />} accent="red" />
          </div>
          <CarryPanel d={d} onSaved={load} />
          <div className="grid gap-6 lg:grid-cols-2">
            <Panel title={<>{t("vat_collected")} · <Money v={d.vat_collected_total} /></>} testid="decl-collected"><RateTable rows={d.vat_collected} t={t} /></Panel>
            <Panel title={<>{t("vat_deductible")} · <Money v={d.vat_deductible_total} /></>} testid="decl-deductible"><RateTable rows={d.vat_deductible} t={t} /></Panel>
            <Panel title={<>{t("wh_operated")} · <Money v={d.withholding_operated_total} /></>} testid="decl-wh-operated"><WhTable rows={d.withholding_operated} t={t} /></Panel>
            <Panel title={<>{t("wh_suffered")} · <Money v={d.withholding_suffered_total} /></>} testid="decl-wh-suffered"><WhTable rows={d.withholding_suffered} t={t} /></Panel>
          </div>
        </>
      )}
      <Panel title={t("history")}>
        {history.length === 0 ? <Empty /> : history.map((h) => (
          <button key={h.id} onClick={() => setMonth(h.month)} className="flex w-full items-center gap-3 border-b border-slate-50 py-2 text-sm hover:bg-slate-50" data-testid={`decl-history-${h.month}`}>
            <span className="num font-semibold">{h.month}</span><span className="text-xs text-slate-500">{dmyTime(h.filed_at)} · {h.filed_by} {h.reference && `· ${h.reference}`}</span>
            <Money v={h.total_to_pay} className="ms-auto" />
          </button>
        ))}
      </Panel>
    </>
  );
}
